import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import withRouter from "components/utils/withRouter.js";
import styles from "styles/public.module.css";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import SnackbarConstants from "utils/constants/Snackbar.js";
import PasswordService from "services/PasswordService.js";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputErrorMessage from "components/utils/InputErrorMessage.js";
import RegexUtils from "utils/RegexUtils.js";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import AuthFormShell from "components/auth/AuthFormShell.js";
import AuthFormActions from "components/auth/AuthFormActions.js";

const RESET_STEPS = ["Email", "Verify", "Password"];

const RESET_SUBTITLES = [
  "We'll send a verification code to your email",
  "Enter the code to continue",
  "Choose a new password for your account",
];

function ResetPassword(props) {
  const { enqueueSnackbar } = useSnackbar();
  const [resetPasswordViewState, setResetPasswordViewState] = useState(0);
  const [isFormProcessing, setIsFormProcessing] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showOtpTimer, setShowOtpTimer] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(30);
  let timer;

  const {
    register,
    formState: { errors },
    getValues,
    handleSubmit,
  } = useForm({ criteriaMode: "all", mode: "onTouched" });

  useEffect(() => {
    return () => {
      stopOtpTimer();
    };
  }, []);

  async function handleFormSubmit(data) {
    setIsFormProcessing(true);
    if (resetPasswordViewState === 0) {
      try {
        const captchaToken = await executeRecaptcha("Request_OTP");
        const response = await PasswordService.requestOtp(
          {
            email: data.email,
          },
          captchaToken
        );
        setTransactionId(response.data.transactionId);
        setResetPasswordViewState(1);
        startOtpTimer();
        enqueueSnackbar(response.message, SnackbarConstants.success);
      } catch (error) {
        handleError(error);
      }
    } else if (resetPasswordViewState === 1) {
      try {
        const captchaToken = await executeRecaptcha("Verify_OTP");
        const response = await PasswordService.verifyOtp(
          {
            email: data.email,
            otp: data.otp,
            transactionId,
          },
          captchaToken
        );
        setTransactionId(response.data.transactionId);
        setResetPasswordViewState(2);
        enqueueSnackbar(response.message, SnackbarConstants.success);
      } catch (error) {
        handleError(error);
      }
    } else {
      try {
        const captchaToken = await executeRecaptcha("Reset_Password");
        const response = await PasswordService.resetPassword(
          {
            ...data,
            transactionId,
          },
          captchaToken
        );
        enqueueSnackbar(response.message, SnackbarConstants.success);
        props.navigate("/");
      } catch (error) {
        handleError(error);
      }
    }
    setIsFormProcessing(false);
  }

  function handleError(error) {
    let errorData = error.data;
    enqueueSnackbar(errorData.message, SnackbarConstants.error);
    let errors = errorData.errors;
    if (errors) {
      errors.forEach((item, i) => {
        enqueueSnackbar(item, SnackbarConstants.error);
      });
    }
  }

  async function resendOTP() {
    setIsFormProcessing(true);
    startOtpTimer();

    try {
      const captchaToken = await executeRecaptcha("Request_OTP");

      const data = getValues();

      const response = await PasswordService.requestOtp(
        {
          email: data.email,
        },
        captchaToken
      );
      setTransactionId(response.data.transactionId);
      enqueueSnackbar("OTP resent", SnackbarConstants.success);
    } catch (error) {
      stopOtpTimer();
      handleError(error);
    }
    setIsFormProcessing(false);
  }

  function startOtpTimer() {
    setShowOtpTimer(true);
    setTimerSeconds(30);

    timer = setInterval(() => {
      setTimerSeconds((prevSeconds) => {
        if (prevSeconds == 0) {
          stopOtpTimer();
        }
        return prevSeconds - 1;
      });
    }, 1000);
  }

  function stopOtpTimer() {
    setShowOtpTimer(false);
    clearInterval(timer);
  }

  return (
    <AuthFormShell
      title="Reset password"
      subtitle={RESET_SUBTITLES[resetPasswordViewState]}
      currentStep={resetPasswordViewState}
      steps={RESET_STEPS}
      footer={
        <Typography className={styles["signup-guidance"]}>
          Remember your password?{" "}
          <Link
            component="button"
            underline="hover"
            onClick={() => props.navigate("/account")}
          >
            Back to sign in
          </Link>
        </Typography>
      }
    >
      <form
        className={styles["auth-form"]}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="mb-3">
          <label htmlFor="emailInput" className="form-label">
            Email ID
          </label>
          <input
            type="email"
            name="email"
            className="form-control"
            id="emailInput"
            aria-describedby="emailHelp"
            placeholder="name@example.com"
            {...register("email", {
              required: "Email ID is required",
              pattern: {
                value: RegexUtils.EMAIL,
                message: "Email ID is invalid",
              },
            })}
            disabled={resetPasswordViewState !== 0}
          />
          <InputErrorMessage name="email" errors={errors} />
        </div>
        {resetPasswordViewState === 1 ? (
          <>
            <div className="mb-3">
              <label htmlFor="otpInput" className="form-label">
                OTP
              </label>
              <input
                type="number"
                name="otp"
                className="form-control"
                id="otpInput"
                aria-describedby="otpHelp"
                placeholder="Enter 6-digit code"
                {...register("otp", {
                  required: "OTP is required",
                  pattern: {
                    value: RegexUtils.OTP,
                    message: "OTP is invalid",
                  },
                })}
              />
              <InputErrorMessage name="otp" errors={errors} />
              <div id="otpHelp" className="form-text">
                OTP will be sent to your email and associated phone number.
              </div>
            </div>
            <div className={styles["otp-actions"]}>
              {showOtpTimer ? (
                <span className={styles["otp-timer"]}>
                  Resend OTP in {timerSeconds}s
                </span>
              ) : (
                <Link
                  component="button"
                  className={styles["support-link"]}
                  onClick={resendOTP}
                  underline="hover"
                >
                  Resend OTP
                </Link>
              )}
            </div>
          </>
        ) : null}
        {resetPasswordViewState === 2 ? (
          <div>
            <div className="mb-3">
              <label htmlFor="newPasswordInput" className="form-label">
                New Password
              </label>
              <div>
                <OutlinedInput
                  name="newPassword"
                  id="newPasswordInput"
                  className={styles["password-input"]}
                  autoComplete="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowNewPassword((show) => !show)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  {...register("newPassword", {
                    required: "New Password is required",
                    pattern: {
                      value: RegexUtils.PASSWORD,
                      message: "New Password is invalid",
                    },
                  })}
                />
              </div>
              <InputErrorMessage name="newPassword" errors={errors} />
              <div id="passwordHelpBlock" className="form-text">
                At least 8 characters with one letter, one number, and one
                special character.
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="confirmPasswordInput" className="form-label">
                Confirm Password
              </label>
              <div>
                <OutlinedInput
                  name="confirmPassword"
                  id="confirmPasswordInput"
                  className={styles["password-input"]}
                  autoComplete="new-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword((show) => !show)}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  {...register("confirmPassword", {
                    required: "Confirm Password is required",
                    pattern: {
                      value: RegexUtils.PASSWORD,
                      message: "Confirm Password is invalid",
                    },
                  })}
                />
                <InputErrorMessage name="confirmPassword" errors={errors} />
              </div>
            </div>
          </div>
        ) : null}
        <AuthFormActions
          submitLabel={getSubmitButtonText()}
          isProcessing={isFormProcessing}
          onCancel={() => props.navigate("/account")}
        />
      </form>
    </AuthFormShell>
  );

  function getSubmitButtonText() {
    if (resetPasswordViewState === 0) return "Send code";
    if (resetPasswordViewState === 1) return "Verify";
    return "Reset password";
  }
}

export default withRouter(ResetPassword);
